import axios from 'axios';
import { createAuthMiddleware } from 'api/middleware/authMiddleware';
import store from 'store';
import { SESSION_KEYS } from 'utils/constant';

const { AUTH_TOKEN } = SESSION_KEYS;

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';

// Create a function to initialize the API with navigation
export const initializeApi = (navigate = null) => {
  const authMiddleware = createAuthMiddleware(store, navigate);

  const api = axios.create({
    baseURL: `${API_BASE_URL}/${API_VERSION}`,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });

  api.interceptors.request.use(
    (config) => {
      // Add Authorization header as fallback for incognito mode
      // Try sessionStorage first (more secure), then localStorage (backward compatibility)
      const token =
        sessionStorage.getItem(AUTH_TOKEN) || localStorage.getItem(AUTH_TOKEN);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  api.interceptors.response.use(
    (response) => {
      // Store token from X-Auth-Token header if present (for incognito mode)
      const headerToken = response.headers['x-auth-token'];
      if (headerToken) {
        // Use sessionStorage for better security (cleared on tab close)
        // Consider encrypting the token before storage in production
        sessionStorage.setItem(AUTH_TOKEN, headerToken);
        // Remove from localStorage if it exists (migration)
        localStorage.removeItem(AUTH_TOKEN);
      }
      return response;
    },
    async (error) => {
      if (error.response?.status === 401) {
        // Clear stored tokens on 401
        sessionStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem(AUTH_TOKEN);
        authMiddleware.onUnauthorized();
      } else {
        authMiddleware.onError(error);
      }
      return Promise.reject(error);
    }
  );

  api.stream = async (url, data, callbacks = {}) => {
    const { onMessage, onError, onComplete } = callbacks;

    try {
      const response = await api({
        method: 'POST',
        url: url,
        data: data,
        headers: {
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
          // Add Authorization header for streaming requests too
          ...((sessionStorage.getItem(AUTH_TOKEN) ||
            localStorage.getItem(AUTH_TOKEN)) && {
            Authorization: `Bearer ${
              sessionStorage.getItem(AUTH_TOKEN) ||
              localStorage.getItem(AUTH_TOKEN)
            }`,
          }),
        },
        responseType: 'stream',
        adapter: 'fetch',
        timeout: 0,
      });

      const stream = response.data;

      if (!stream || !stream.pipeThrough) {
        throw new Error('Invalid stream response from server');
      }

      const reader = stream.pipeThrough(new TextDecoderStream()).getReader();

      while (true) {
        const { value, done } = await reader.read();

        if (done) {
          onComplete && onComplete();
          break;
        }

        if (value) {
          if (onMessage) {
            onMessage(value);
          }
        }
      }

      return response;
    } catch (error) {
      if (onError) {
        onError(error);
      }
      throw error;
    }
  };

  return api;
};

export default initializeApi();
